import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Store, Clock, AlertCircle, CheckCircle2, Copy, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Cargar Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const OxxoPaymentForm = ({ clientSecret, amount, description }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !clientSecret) {
      return;
    }

    if (!email || !name) {
      toast.error('Por favor completa tu nombre y email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmOxxoPayment(
        clientSecret,
        {
          payment_method: {
            billing_details: {
              name: name,
              email: email,
            },
          },
        }
      );

      if (error) {
        setError(error.message);
        toast.error(error.message);
      } else if (paymentIntent) {
        // El pago fue procesado, obtener el voucher
        if (paymentIntent.next_action?.oxxo_display_details) {
          setVoucherUrl(paymentIntent.next_action.oxxo_display_details.hosted_voucher_url);
          toast.success('¡Voucher generado! Ahora puedes pagar en OXXO.');
        }
      }
    } catch (err) {
      setError('Error al procesar el pago');
      toast.error('Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (voucherUrl) {
    return (
      <div className="text-center space-y-6">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">¡Voucher Generado!</h2>
          <p className="text-white/70">Presenta este voucher en cualquier tienda OXXO para completar tu pago.</p>
        </div>

        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
          <p className="text-orange-400 font-semibold mb-2">Monto a pagar:</p>
          <p className="text-3xl font-bold text-white">${amount} MXN</p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => window.open(voucherUrl, '_blank')}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
          >
            <Store className="w-5 h-5 mr-2" />
            Ver Voucher para OXXO
          </Button>
          
          <Button
            onClick={() => {
              navigator.clipboard.writeText(voucherUrl);
              toast.success('¡Enlace del voucher copiado!');
            }}
            variant="outline"
            className="w-full border-white/20 text-white"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar Enlace del Voucher
          </Button>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-left">
          <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Instrucciones
          </h4>
          <ul className="text-white/70 text-sm space-y-2">
            <li>1. Descarga o imprime tu voucher</li>
            <li>2. Llévalo a cualquier tienda OXXO</li>
            <li>3. Indica al cajero que deseas pagar un servicio</li>
            <li>4. Presenta el código de barras o número de referencia</li>
            <li>5. Paga el monto indicado en efectivo</li>
            <li>6. Tu membresía se activará automáticamente en 24 horas</li>
          </ul>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <p className="text-amber-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            El voucher expira en 3 días. Paga antes de esa fecha.
          </p>
        </div>

        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 text-center">
        <Store className="w-12 h-12 text-orange-400 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white mb-1">Pago en OXXO</h3>
        <p className="text-white/70">{description}</p>
        <p className="text-3xl font-bold text-orange-400 mt-3">${amount} MXN</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-white/70 text-sm mb-2">Nombre completo</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre como aparece en tu identificación"
            className="w-full bg-black/30 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40"
            required
          />
        </div>
        
        <div>
          <label className="block text-white/70 text-sm mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            className="w-full bg-black/30 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40"
            required
          />
          <p className="text-white/50 text-xs mt-1">Recibirás confirmación en este email</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      <Button
        type="submit"
        disabled={loading || !stripe}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
      >
        {loading ? (
          <>
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            Generando voucher...
          </>
        ) : (
          <>
            <Store className="w-5 h-5 mr-2" />
            Generar Voucher OXXO
          </>
        )}
      </Button>

      <Button
        type="button"
        onClick={() => navigate('/dashboard')}
        variant="ghost"
        className="w-full text-white/60 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Cancelar y volver
      </Button>
    </form>
  );
};

const PaymentOxxo = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  
  const amount = searchParams.get('amount') || '199';
  const description = searchParams.get('description') || 'Membresía ViubuxConnect';

  useEffect(() => {
    // Recuperar el client_secret del localStorage
    const secret = localStorage.getItem('oxxo_client_secret');
    if (secret) {
      setClientSecret(secret);
    } else {
      toast.error('Sesión de pago no encontrada');
      navigate('/dashboard');
    }
  }, [navigate]);

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] to-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] to-[#1a1a2e] py-12 px-4">
      <div className="max-w-md mx-auto">
        <Card className="bg-[#1a1a2e] border-orange-500/30">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white text-center flex items-center justify-center gap-2">
              <Store className="w-6 h-6 text-orange-400" />
              Pago con OXXO
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <OxxoPaymentForm 
                clientSecret={clientSecret}
                amount={amount}
                description={description}
              />
            </Elements>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentOxxo;
